/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2015-2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

angular.module('bulbApp.pageViewModule')
  .controller('PageViewController', [
    '$',
    '$interval',
    '$document',
    '$location',
    '$q',
    '$rootScope',
    '$scope',
    '$sce',
    '$state',
    '$timeout',
    '$translate',
    'Restangular',
    'BulbAssetLibrarySnapshotPdfService',
    'BulbAuthService',
    'BulbBusyService',
    'BulbCoverImageService',
    'BulbErrorService',
    'BulbModalService',
    'BulbPageService',
    'BulbPresentationService',
    'BulbRecommendService',
    'BulbUnitService',
    'SerialSaveService',
    'TourService',
    'UnitStateStore',
    'AUTHOR_TABS',
    'CONFIG',
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    PageViewController,
  ]);

/**
 * AngularJS controller for the content portion of the page unit view.
 */
function PageViewController(
  $,
  $interval,
  $document,
  $location,
  $q,
  $rootScope,
  $scope,
  $sce,
  $state,
  $timeout,
  $translate,
  Restangular,
  BulbAssetLibrarySnapshotPdfService,
  BulbAuthService,
  BulbBusyService,
  BulbCoverImageService,
  BulbErrorService,
  BulbModalService,
  BulbPageService,
  BulbPresentationService,
  BulbRecommendService,
  BulbUnitService,
  SerialSaveService,
  TourService,
  UnitStateStore,
  AUTHOR_TABS,
  CONFIG,
) {
  const that = this;

  this.page = BulbPageService.page;
  this.navigationAwayApproval = false;

  this.previewStateActive = ($state.current.name === 'preview_page' || $state.current.name === 'live_page');

  this.ckeStatus = this.page.unit.ckeStatus;
  this.page.mode = $state.current.data.mode || 'default';
  this.page.editing = this.page.canEdit && this.page.mode === 'default';
  this.snapshotView = $state.current.data.snapshotView || false;
  this.isLoggedIn = BulbAuthService.currentUser.isAuthenticated();
  this.languageKey = (BulbAuthService.currentUser.language)?.toLowerCase();
  this.acceptedImageFileExtensions = CONFIG.image.extensions;
  this.isOwner = BulbAuthService.currentUser.username === this.page.unit.ownerUsername;
  this.allowed = BulbAuthService.currentUser.allowed;

  document.addEventListener('mouseup', BulbPageService.addMarginToListForMediumEditor);
  document.addEventListener('keyup', BulbPageService.addMarginToListForMediumEditor);

  const ShareTransition = UnitStateStore.getShareTransitionEnum();

  // Fetching the parent data for composite units
  BulbUnitService.fetchParent().then(() => {
    that.parentUnit = BulbUnitService.parentUnit;
    that.previousUnit = BulbUnitService.previousSibling;
    that.nextUnit = BulbUnitService.nextSibling;
  });

  // Set revision to intercommunication between multiple tabs while auto-save content.
  // eslint-disable-next-line no-param-reassign
  $rootScope.revision = { id: this.page.unit.id, rev: this.page.unit.rev };

  // Disable drag and drop resources ont a page
  $('body').on('dragover drop', (event) => {
    event.preventDefault();
    event.stopPropagation();
  });

  if (that.page.editing === false) {
    let counterForPreviewPage = 0;

    // Adding the Aria-label
    const watchForPreviewPageRenderFinish = $interval(() => {
      if (counterForPreviewPage < 20) {
        counterForPreviewPage += 1;
        const $pageContentDiv = $('.bulb-page-content');
        if ($pageContentDiv.length === 1) {
          $translate('pageview_content_label').then((label) => {
            $pageContentDiv.attr('aria-label', label);
            $interval.cancel(watchForPreviewPageRenderFinish);
          });
        }
      } else if (counterForPreviewPage >= 20) {
        $interval.cancel(watchForPreviewPageRenderFinish);
      }
    }, 100);
  }

  const destroyUpdatePageDetailsHandler = $rootScope.$on('update-page-details', () => {
    // If page details change, we have to update the editing value, as it will have been reset to false.
    that.page = BulbPageService.page;
    that.page.mode = $state.current.data.mode || 'default';
    that.page.editing = that.page.canEdit && that.page.mode === 'default';
  });

  $scope.$on('$destroy', destroyUpdatePageDetailsHandler);

  /*
   * This code warns the user about unpublished changes if they try to navigate away from an edited page.
   * It listens for an event which is broadcast from the DeletePanelController to correctly set its local variable.
   * Additionally, an event is broadcast from here that allows the page totem to toggle the 'publish' panel after a
   * user chooses that option from a state change.
   */
  if (this.page.canEdit) {
    $scope.$on('navigationAwayApproval', (event, data) => {
      /**
       * The 'navigationAwayApproval' flag is used to display to a user whether to publish-later OR publish-now
       * modal. This flag not need to be explicitly set in some instances -- such as when a user loads a page initially,
       * because 'navigationAwayApproval' flag will be set to 'false' and thus, used down in the if statement
       * will inherently possess the correct logic.
       */
      that.navigationAwayApproval = data;
    });
    // Publish later modal logic:
    $scope.$on('$stateChangeStart', (event, toState, toParams, fromState, fromParams) => {
      if ((fromState.name === 'unit.page' && toState.name === 'preview_page')
        || fromState.name === 'preview_page' && toState.name === 'unit') {
        resolveDuplicateIds();
      }
      if (fromParams.unitPath !== toParams.unitPath
        && !(that.navigationAwayApproval)
        && (that.shareStatus === 'private')
        && !(fromState.name === 'live_page' || fromState.name === 'preview_page'
          || toState.name === 'live_page' || toState.name === 'preview_page'
          || fromState.name === 'presentation_page' || toState.name === 'presentation_page'
          || toState.name === 'snapshot_preview')) {
        event.preventDefault();
        that.handleNavigationFromUnpublishedPage(toState, toParams);
      }
    });
  }

  /**
   * Handler for the unpublished page modal and whatever decisions the user makes.
   * @param toState
   * @param toParams
   */
  this.handleNavigationFromUnpublishedPage = function handleNavigationFromUnpublishedPage(toState, toParams) {
    // Check if the user does not have unlimited publishing
    if (BulbAuthService.currentUser.features.PublishingFeature.maxPublishedPages !== -1) {
      // Check if the user has space to publish more pages
      // Or if the page is already published, so they can publish new changes to that page
      if ((BulbAuthService.currentUser.metrics.numPublishedPages
        >= BulbAuthService.currentUser.features.PublishingFeature.maxPublishedPages)
        && !BulbUnitService.currentUnit.isPublished) {
        that.navigationAwayApproval = true;
        $state.go(toState, toParams);
        return;
      }
    }

    const modalOptions = {
      okButtonCode: 'pageview_unpublished_changes_ok',
      cancelButtonCode: 'pageview_unpublished_changes_cancel',
      showCancelButton: true,
      showOkButton: true,
      fontAwesomeCode: 'fas fa-exclamation-triangle',
      windowClass: 'bulb-aero-modal-body bulb-aero-modal-buttons bulb-aero-modal-buttons-confirm',
      titleCode: that.page.unit.isPublished ? 'pageview_modified_changes_title'
        : 'pageview_unpublished_changes_title',
      bodyCode: that.page.unit.isPublished ? 'pageview_modified_changes_description'
        : 'pageview_unpublished_changes_description',
    };
    BulbModalService.show(modalOptions).then(() => {
      const pendingStateChangeOnPublish = {
        toState,
        toParams,
      };
      // Need the timeout here so that broadcasting the event updates the model and controller values.
      // FUTURE TAM: Move logic of toggling certain panels within a service, that way we could inject it both here
      // And in the pageviewheader-controller. This would make it easier to unit test.
      $timeout(() => {
        $rootScope.$broadcast('togglePanel', 'publish', pendingStateChangeOnPublish);
      });
    }, () => {
      that.navigationAwayApproval = true;
      $state.go(toState, toParams);
    });
  };
  const emptyContent = '<p><br/></p>';

  // Editor doesn't handle undefined content well, so give it an empty line instead
  if (!this.page.unit.content) {
    this.page.unit.content = emptyContent;
  }

  /**
   * Placeholder of medium editor
   */
  this.getPlaceholder = function getPlaceholder() {
    return Foundation.utils.is_small_only() // jshint ignore:line
      ? 'pageview_content_placeholder_short' : 'pageview_content_placeholder';
  };

  if (!this.page.canEdit && this.page.mode !== 'default' && this.page.mode !== 'presentation') {
    $state.go('accessDenied', {}, { location: false });
  }

  if (!this.page.editing) {
    this.trustedContent = $sce.trustAsHtml(this.page.unit.content);
  }

  if (this.page.unit.isPublished) {
    this.shareStatus = this.page.unit.isModified ? 'modified' : 'shared';
  } else {
    this.shareStatus = 'private';
  }

  // Set up the open graph image for this CompositeUnit:
  // NOTE : This doesn't need to be updated dynamically, as the openGraph image is only accessed when viewing the
  // readonly version of a page
  if (that.page.unit.coverImage) {
    this.openGraphImage = that.page.unit.coverImage;
  } else if (that.page.unit.horizontalImage) {
    this.openGraphImage = that.page.unit.horizontalImage;
  }

  /**
   * if snapshot pdf is enabled then call method to update snapshot pdf if page is edit mode.
   * if snapshot pdf is not enabled yet then called method to enabled snapshot option.
   */
  if (this.page.editing) {
    if (BulbAuthService.currentUser.prefs.importingGoogleAssetAsPdfEnabled
      && BulbAuthService.currentUser.connections.googleOAuthToken) {
      BulbAssetLibrarySnapshotPdfService.updateSnapshotPdf(that.page.unit);
    }

    if (BulbAuthService.currentUser.prefs.onBoarding.onboardingShowPageIntro) {
      $timeout(() => {
        TourService.startPageIntro();
      }, 2000);
    } else if (BulbAuthService.currentUser.prefs.onBoarding.onboardingShowPageTour) {
      $timeout(() => {
        TourService.startPageTour();
      }, 2000);
    }
  }

  /**
   * Getter used by autoSave directive.
   * @returns {string} The current content of the page.
   */
  this.getContent = function getContent() {
    return that.page.unit.content;
  };

  this.defaultAutoSaveOptions = {
    restId: that.page.unit.id,
    restCollection: 'units',
  };

  this.contentAutoSaveOptions = angular.copy(this.defaultAutoSaveOptions);
  this.contentAutoSaveOptions.getter = this.getContent;

  /**
   * Returns an author back to the 'edit' version of the page.  Used when the author is viewing a page in 'live' mode.
   */
  this.edit = function edit() {
    $state.go('unit', { unitPath: this.page.unit.path }, { location: 'replace' });
  };

  this.coverImageMode = 'default'; // default or editor
  this.coverImageEdit = null;
  this.isImageEditorActive = false;
  this.isImageEditorMoving = false;
  this.isCoverImageUploadActive = false;
  this.isPresentationMode = BulbPresentationService.isFullScreen();
  this.page.coverImageLayout = 'default';

  /**
   * Configured upload on-file-uploaded handler. See upload-controller.js.
   * When image file has been uploaded, open an imageEditor,
   * with crop type CROP_COVER_IMAGE, for the asset uploaded.
   *
   * The edit object has the following properties:
   *     asset:
   *       id: uploaded asset's id
   *       assetDefinitionsKey: uploaded asset's assetDefinitionsKey,
   *       url: uploaded asset's URL,
   *       width: uploaded asset's width,
   *       height: uploaded asset's height,
   *       center: uploaded asset's already-specified center (optional, used only for edit image),
   *     cropType: Specifier for the type of crop to be performed, interpreted by the imageEditor directive.

   * @param entry The server response from the upload, containing asset information.
   * @returns {boolean} true if successful, false otherwise.
   */
  this.onCoverImageUploaded = function onCoverImageUploaded(entry) {
    const deferred = $q.defer();

    if (entry) {
      const setting = CONFIG.coverimage;

      if (entry.image.width < setting.minwidth || entry.image.height < setting.minheight) {
        deferred.reject({ message: 'invalid image size (too small).' });
        // Then we show the modal, and allow the user to select a new one, or use the current one blurry.
        BulbCoverImageService.calculateCoverImageSize(entry, setting, that, 'pageView');
      } else {
        // If all is good with the image, move it to the editor phase.
        that.coverImageMode = 'editor';
        that.isCoverImageUploadActive = false;

        that.coverImageEdit = {
          isNew: !that.page.unit.coverImage,
          asset: {
            id: entry.image.id,
            assetDefinitionsKey: 'coverImage',
            url: entry.image.url,
            width: entry.image.width,
            height: entry.image.height,
          },
          cropType: 'CROP_COVER_IMAGE',
        };

        deferred.resolve();
      }
    } else {
      deferred.reject({ message: 'invalid entry' });
    }
    const bulbMainDiv = $('.bulb-main');
    const bulbMainParentDiv = bulbMainDiv.parent();
    if (bulbMainParentDiv.hasClass('bulb-aria-hide')) {
      bulbMainParentDiv.removeClass('bulb-aria-hide');
    }
    return deferred.promise;
  };

  /**
   * A method that opens the image picker modal.
  */
  this.openImagePicker = function openImagePicker() {
    $document[0].getElementById('hiddenImagePickerButtonContainer').querySelector('button').click();
  };

  /**
   * Edit existing cover image. Get the original image and current center values from the server.
   * Display the imageEditor with this image, centered as specified,
   * with Cancel and Delete controls and a Save button.
   */
  this.editCoverImage = function editCoverImage() {
    const coverImageId = that.page.unit.coverImage.id;

    BulbBusyService.showBusyDuring(
      // get cover image asset from server
      Restangular.one('images', coverImageId).customGET('originalimage/coverImage')
        .then((coverImage) => {
          that.coverImageEdit = {
            isNew: !that.page.unit.coverImage,
            asset: {
              id: coverImage.id,
              assetDefinitionsKey: 'coverImage',
              url: coverImage.url,
              width: coverImage.width,
              height: coverImage.height,
              center: {
                X: coverImage.centerX,
                Y: coverImage.centerY,
              },
            },
            cropType: 'CROP_COVER_IMAGE',
          };
          that.coverImageMode = 'editor';
          that.isCoverImageUploadActive = false;
        }, (error) => {
          BulbErrorService.error('Cannot get original image for cover image', error);
        }),
    );
  };

  /**
   * Configured imageEditor on-edit-complete handler. See imageEditor-directive.js.
   * When user clicks Save in the imageEditor, process the edit data.
   * If data is set: send new edit data to server, then update composite unit and display the new cover image,
   * and resolve the promise with true.
   * If not, edit was canceled: do nothing, except return to default state, and resolve the promise with true.
   * @param data The image URL and crop information generated by the edit.
   * @returns a promise, which resolves to true or false.
   */
  this.onCoverImageEditComplete = function onCoverImageEditComplete(data) {
    const deferred = $q.defer();
    that.edit = null;
    if (data) {
      const uploadAsset = {
        assetId: data.asset.id,
        assetDefinitionsKey: 'coverImage',
        centerX: data.center.X,
        centerY: data.center.Y,
      };
      SerialSaveService.save({
        save: () => Restangular.one('units', that.page.unit.id)
          .customPOST(uploadAsset, 'coverimage')
          .then((pageUnit) => {
            const updatedUnit = pageUnit.plain();
            BulbUnitService.setCurrentUnit(updatedUnit);
            // FUTURE: some more general way of updating interested components?
            BulbRecommendService.updateRecommend(updatedUnit);
            // Would typically handle the reloading of the state here, but the image editor handles that
            // itself.

            // Update share status to reflect that the page has been edited:
            UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

            deferred.resolve(true);
          }),
      }).catch((error) => {
        const errorMessage = BulbErrorService.error('Cannot update unit cover image', error);
        deferred.reject(errorMessage);
      });
    } else {
      that.coverImageMode = 'default';
      that.isCoverImageUploadActive = false;
      deferred.resolve(true);
    }
    return deferred.promise;
  };

  /**
   * Configured imageEditor on-delete handler. See imageEditor-directive.js.
   * Delete the cover image on the server, and clear the visible cover image,
   * and resolve the promise with true.
   * On error, reject the promise.
   * @returns a promise.
   */
  this.onCoverImageDelete = function onCoverImageDelete() {
    // Delete the cover image
    return SerialSaveService.save({
      save: () => Restangular.one('units', that.page.unit.id)
        .customDELETE('coverimage')
        .then((pageUnit) => {
          const updatedUnit = pageUnit.plain();
          BulbUnitService.setCurrentUnit(updatedUnit);
          // FUTURE: some more general way of updating interested components?
          BulbRecommendService.updateRecommend(updatedUnit);

          // Update share status to reflect that the page has been edited:
          const ShareTransition = UnitStateStore.getShareTransitionEnum();
          UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

          $state.reload();
        }),
    });
  };

  /**
   * Method to confirm the deletion of a cover image and show the busy indicator prior to deleting a cover image.
   * @returns a promise.
   */
  this.deleteCoverImage = function deleteCoverImage() {
    $translate('cover_image_type').then((coverImageText) => {
      const modalTitle = $translate.instant('modal_delete_confirm_title', { type: coverImageText });
      $translate('modal_delete_confirm_body', { type: coverImageText }).then((bodyText) => {
        BulbModalService.show({
          titleCode: modalTitle,
          bodyText,
          fontAwesomeCode: 'far fa-trash-alt',
          windowClass: 'bulb-aero-modal-body bulb-aero-modal-buttons bulb-aero-modal-buttons-danger',
          okButtonCode: 'button_delete',
        }).then(() => {
          // user confirmed the confirm delete modal
          BulbBusyService.showBusyDuring(
            that.onCoverImageDelete(),
          );
        });
      });
    });
  };

  /**
   * Method for converting a value to be url-safe.
   * Used for creating a URL to updating the address bar when the page title is updated.
   * @param value Value for being made url-safe.
   * @returns {string} Url-safe version of the value.
   */
  function replaceSpecialCharacters(value) {
    if (!value) {
      return '';
    }

    // Convert title to lower-case and remove trailing spaces
    let replaceString = value.toLowerCase().trim();

    // Remove all URL reserved characters as well as bulb reserved characters.
    // URL specification reserved characters are the following: :/?#[]@!$&'()*+,;=
    // bulb reserved characters are the following: .~\"<>%{}|^`
    // Characters which require escape chars within the regex pattern: .^$*+?()[{\|^-]\
    replaceString = replaceString.replace(/[:\/?#\\[\]@!\$&\'\(\)\*\+,;=.~"<>%\{}\|\^`]/g, ' ');

    // Replace anything underscores and whitespace with a dash
    replaceString = replaceString.replace(/_\s/g, '-');

    // Remove unicode replacement characters.
    replaceString = replaceString.replace(/\\ufffd/g, '-');

    // Replace multiple instances of dashes with a single dash
    replaceString = replaceString.replace(/-+/g, '-');

    // Remove leading or trailing dashes
    replaceString = replaceString.replace(/^-+/g, '');
    replaceString = replaceString.replace(/-+$/g, '');

    return replaceString;
  }

  that.pageHeaderUpdate = function pageHeaderUpdate(pageUnit) {
    // Updating the page name here is required to keep the page path logic in one place (this controller).
    that.page.unit.name = pageUnit.name;
  };

  // Watch for changes to the unit name, and calculate a new page path (url) for the page if necessary.
  $scope.$watch('pageViewCtrl.page.unit.name', (value, oldValue) => {
    if (value === oldValue) return;
    UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

    let path = '';

    // If the unitType is that of an ArchivePageUnit, we handle the path differently.
    if (that.page.unit.unitType === 'ArchivePageUnit') {
      const archivePath = `${replaceSpecialCharacters(value)}~a${that.page.unit.id}`;
      // Update the address bar to reflect any path changes without reloading the state.
      // See $location decorator in app.js for skipReload().
      path = `/u/${archivePath}`;
      if ($location.path() !== path) {
        $location.skipReload().path(path).replace();
      }
    } else if (that.page.unit.unitType !== 'PublishedPageUnit' && !that.page.unit.isPublished) {
      // Get a clean version of the page title and append the unit id:
      const draftPath = `${replaceSpecialCharacters(value)}~i${that.page.unit.id}`;

      // NOTE : We are not encoding the string so that it looks better in the address bar, and our supported
      // browsers support it.  However, this is left here in case we run into a situation indicating that we
      // should be encoding because URLs 'technically' cannot contain unicode characters, only ASCII, even though
      // virtually all modern browsers support it.
      // URL Encode the draftPath:
      // draftPath = encodeURIComponent(draftPath);

      // Update the address bar to reflect any path changes without reloading the state.
      // See $location decorator in app.js for skipReload().
      path = `/u/${draftPath}`;
      if ($location.path() !== path) {
        $location.skipReload().path(path).replace();
      }
    }
  });

  // Does a patch to the /unit/{unitId}/viewcount
  function updateViewCount() {
    if (!that.isOwner) {
      Restangular.one(`/unit/${that.unit.id}/viewcount`).patch();
    }
  }

  // 15 Seconds in milliseconds.
  const timeOutDelay = 15000;
  // Waits 15 seconds before calling the update viewViewCount method.
  // This is to avoid spam refreshing pages to gain views.
  const updateViewCountResponse = $timeout(updateViewCount, timeOutDelay);

  // Listen for updates to the current unit share status, and update the local share status when it happens
  const shareStatusSub = UnitStateStore.shareStatus$.subscribe((shareStatus) => {
      this.shareStatus = shareStatus;
  });

  $scope.$on('$destroy', () => {
    $timeout.cancel(updateViewCountResponse);
    shareStatusSub.unsubscribe();
    document.removeEventListener('mouseup', BulbPageService.addMarginToListForMediumEditor);
    document.removeEventListener('keyup', BulbPageService.addMarginToListForMediumEditor);
  });

  /**
   * Function that modifies duplicate ids to unique ids of medium editor
   */
  function resolveDuplicateIds() {
    // hiding from narrator auto generating 'medium editor' and 'medium editor toolbar'
    const mediumEditorAnchorPreviews = $('div[id*="medium-editor-anchor-preview"]');
    for (let i = 0; i < mediumEditorAnchorPreviews.length; i += 1) {
      const mediumEditorAnchorPreview = $(mediumEditorAnchorPreviews[i]);
      mediumEditorAnchorPreview.attr('aria-hidden', 'true');
      // handling unique ids
      const mediumEditorToolbarAnchorPreview = mediumEditorAnchorPreview.find('div');
      const anchorId = mediumEditorAnchorPreview.attr('id').split('-').slice(-1).pop();
      const newId = `medium-editor-toolbar-anchor-preview${anchorId}`;
      if (mediumEditorToolbarAnchorPreview.length === 1
        && mediumEditorToolbarAnchorPreview.attr('id') !== newId) {
        mediumEditorToolbarAnchorPreview.attr('id', newId);
      }
    }
    const mediumEditorToolbars = $('div[id*="medium-editor-toolbar"]');
    for (let j = 0; j < mediumEditorToolbars.length; j += 1) {
      const mediumEditorToolbar = $(mediumEditorToolbars[j]);
      mediumEditorToolbar.attr('aria-hidden', 'true');
    }
  }

  /**
  * checking every 100ms to see when the autosize template has been rendered and then fixing it for accessibility.
  * Adding ARIA-LABEL for AutoSize JS
  */
  let counter = 0;
  const watchForRenderFinish = $interval(() => {
    if (counter < 20) {
      counter += 1;
      const autoSizeTemplate = $('#autosizejs');
      if (autoSizeTemplate.length === 1) {
        // Adding dummy label and hiding since this is not a visible control and not going to be read by narrator
        // for AutoSize JS Markup
        autoSizeTemplate.attr('aria-label', 'dummy-label');
        autoSizeTemplate.attr('aria-hidden', 'true');
        resolveDuplicateIds();
        $interval.cancel(watchForRenderFinish);
      }
    } else if (counter >= 20) {
      $interval.cancel(watchForRenderFinish);
    }
  }, 100);
}
